import * as React from 'react';

import { useProfilesData } from '@/features/user/api';
import type { NaplanBatch } from '@/features/user-notifications/api/get-batches';
import { useBatches } from '@/features/user-notifications/api/queries';
import {
  NAPLAN_PRODUCT_ID,
  NAPLAN_GRADES,
  GRADE_TO_NAPLAN_IDS_MAPPING,
} from '@/features/user-notifications/utils/constants';
import { useAnalytics } from '@/lib/analytics/hooks/use-analytics';
import { useIsSmallScreen } from '@/lib/hooks';

import { EnrollmentForm } from '../enrollments/enrollment-form';
import { SuccessView } from '../enrollments/success-view';

/**
 * Duration in milliseconds to display the success view before triggering any follow-up actions
 */
const SUCCESS_DISPLAY_DURATION_MS = 3000;

/**
 * Type for simple enrollment hooks (School Curriculum, IMO)
 * These enrollments only require a grade selection
 */
interface SimpleEnrollmentHook {
  enroll: (grade: string, options?: { onSuccess?: () => void }) => void;
  isEnrolling: boolean;
}

/**
 * Type for NAPLAN enrollment hook
 * NAPLAN enrollments require grade, batch_id, and variant_id
 */
interface NaplanEnrollmentHook {
  enroll: (
    params: {
      grade: string;
      batch_id?: string;
      variant_id?: string;
    },
    options?: { onSuccess?: () => void }
  ) => void;
  isEnrolling: boolean;
}

/**
 * Union type for all enrollment hooks
 */
type EnrollmentHook = SimpleEnrollmentHook | NaplanEnrollmentHook;

/**
 * Props for the EnrollmentDrawerContent component
 *
 * This component handles enrollment flows for different enrollment types:
 * - School Curriculum: Simple grade-based enrollment
 * - IMO: Simple grade-based enrollment
 * - NAPLAN: Complex enrollment requiring grade, batch, and variant selection
 *
 * @note The useEnrollmentHook function prop pattern is used to maintain flexibility
 * across different enrollment types while keeping the component reusable.
 */
interface EnrollmentDrawerContentProps {
  /** Description text shown to the user explaining the enrollment */
  description: string;
  /** List of available grades the user can select from */
  availableGrades: string[];
  /**
   * Function that returns an enrollment hook
   * - For School Curriculum/IMO: Returns SimpleEnrollmentHook
   * - For NAPLAN: Returns NaplanEnrollmentHook
   */
  useEnrollmentHook: () => EnrollmentHook;
  /** Callback to close the drawer */
  onClose: () => void;
  /** Type of enrollment being performed - determines the enrollment flow and analytics */
  enrollmentType: 'school-curriculum' | 'naplan' | 'imo';
}

/**
 * Shared enrollment drawer content component
 *
 * Handles the enrollment form UI and logic for School Curriculum, IMO, and NAPLAN enrollments.
 * NAPLAN enrollments require additional batch selection step.
 *
 * @note This component does NOT trigger page reloads - it relies on TanStack Query invalidation
 * in the enrollment mutation hooks to update the UI.
 */
export const EnrollmentDrawerContent: React.FC<
  EnrollmentDrawerContentProps
> = ({
  description,
  availableGrades,
  useEnrollmentHook,
  onClose,
  enrollmentType,
}) => {
  const [selectedGrade, setSelectedGrade] = React.useState<string>('');
  const [selectedBatch, setSelectedBatch] = React.useState<NaplanBatch | null>(
    null
  );
  const [showSuccess, setShowSuccess] = React.useState(false);
  const isMobile = useIsSmallScreen();
  const { trackEvent } = useAnalytics();

  const isNaplan = enrollmentType === 'naplan';
  const { parentEmail } = useProfilesData();
  const enrollmentHook = useEnrollmentHook();
  const { enroll: enrollToCourse, isEnrolling: isPending } = enrollmentHook;

  // Get NAPLAN grade value for API call
  const naplanGradeValue = React.useMemo((): string => {
    if (!isNaplan || !selectedGrade) return '';
    const gradeMap = NAPLAN_GRADES as Record<string, string>;
    return gradeMap[selectedGrade] ?? '';
  }, [isNaplan, selectedGrade]);

  // Fetch batches for NAPLAN when grade is selected
  const gradeMapping = GRADE_TO_NAPLAN_IDS_MAPPING as Record<
    string,
    { variantId: string; gradeName: string }
  >;
  const variantId =
    selectedGrade && isNaplan && selectedGrade in gradeMapping
      ? gradeMapping[selectedGrade]?.variantId
      : undefined;

  const batchesQueryResult = useBatches({
    grade: naplanGradeValue,
    product_id: NAPLAN_PRODUCT_ID,
    variant_id: variantId,
  });
  const batchesData = batchesQueryResult.data;
  const isLoadingBatches = batchesQueryResult.isLoading;
  const isBatchesError = batchesQueryResult.isError;
  const batches = batchesData?.batches ?? [];

  // Reset batch selection when grade changes
  React.useEffect(() => {
    setSelectedBatch(null);
  }, [selectedGrade]);

  /**
   * Analytics callback when user selects a batch
   * Note: Actual batch state update is handled by setSelectedBatch prop in EnrollmentForm
   * This callback is solely for tracking user interactions
   */
  const handleBatchSelect = React.useCallback(
    (batch: NaplanBatch | null) => {
      setSelectedBatch(batch);
      if (batch && isNaplan) {
        trackEvent('NaplanPopupClickedSelectBatch', 1, {
          clickType: 'cta',
          clickCTA: 'Select Batch',
          clickSection: 'Naplan Section',
        });
      }
    },
    [isNaplan, trackEvent]
  );

  const handleEnroll = () => {
    if (!selectedGrade || !parentEmail) return;

    if (isNaplan) {
      // Track analytics for NAPLAN enrollment
      trackEvent('NaplanPopupClickedEnrollNow', 1, {
        clickType: 'cta',
        clickCTA: 'Enroll now Naplan',
        clickSection: 'Naplan Section',
      });

      if (!selectedBatch) return;

      const variant_id = selectedBatch.variant_id ?? '';
      // When isNaplan is true, we know enrollToCourse accepts NAPLAN params
      const naplanEnroll = enrollToCourse as NaplanEnrollmentHook['enroll'];
      naplanEnroll(
        {
          grade: selectedGrade,
          batch_id: selectedBatch.batch_id,
          variant_id,
        },
        {
          onSuccess: () => setShowSuccess(true),
        }
      );
    } else {
      // Track analytics based on enrollment type
      if (enrollmentType === 'imo') {
        trackEvent('IMOPopupClickedEnrollNow', 1, {
          clickType: 'cta',
          clickCTA: 'Enroll Now IMO',
          clickSection: 'IMO Section',
        });
      } else if (enrollmentType === 'school-curriculum') {
        trackEvent('SchoolCurriculumPopupClickedEnrollNow', 1, {
          clickType: 'cta',
          clickCTA: 'Enroll Now School Curriculum',
          clickSection: 'School Curriculum Section',
        });
      }

      // When isNaplan is false, we know enrollToCourse accepts simple params
      const simpleEnroll = enrollToCourse as SimpleEnrollmentHook['enroll'];
      simpleEnroll(selectedGrade, {
        onSuccess: () => setShowSuccess(true),
      });
    }
  };

  /**
   * Auto-close the drawer after SUCCESS_DISPLAY_DURATION_MS
   * Query invalidation in the mutation hook will refresh the data
   * Cleanup timeout if component unmounts to prevent memory leaks
   */
  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (showSuccess) {
      timeoutId = setTimeout(() => {
        onClose();
      }, SUCCESS_DISPLAY_DURATION_MS);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showSuccess, onClose]);

  if (showSuccess) {
    return (
      <div>
        <SuccessView inline={true} isMobile={isMobile} onClose={onClose} />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <EnrollmentForm
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
        isPending={isPending}
        handleEnroll={handleEnroll}
        isMobile={isMobile}
        description={description}
        availableGrades={availableGrades}
        showBatchSelection={isNaplan}
        batches={batches}
        selectedBatch={selectedBatch}
        setSelectedBatch={setSelectedBatch}
        isLoadingBatches={isLoadingBatches}
        isBatchesError={isBatchesError}
        onBatchSelect={handleBatchSelect}
      />
    </div>
  );
};
